/**
 * SDCPaymentPage
 * --------------
 * Private, link-gated payment page for approved SDC applicants.
 *
 * Flow:
 *  1. Admin sends the applicant a unique URL (/sdc-payment/:token).
 *  2. Applicant lands here, sees their name, picks a payment plan, and accepts T&Cs.
 *  3. We record acceptance (+ ip, plan, timestamp) on the backend.
 *  4. Backend returns the matching events.sisterdiamonds.com checkout URL.
 *  5. Frontend forwards them there.
 *
 * Page is intentionally unlinked from the rest of the site (no nav, no SEO).
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2, ShieldCheck, Crown, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SDCPaymentPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedProgram, setAcceptedProgram] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = 'Sister Diamonds Collective — Private Payment Page';
    // Discourage indexing of this private page
    let robotsTag = document.querySelector('meta[name="robots"]');
    const previous = robotsTag?.getAttribute('content') ?? null;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    robotsTag.setAttribute('content', 'noindex,nofollow');
    return () => {
      // Restore on unmount so other pages aren't accidentally noindexed
      if (previous !== null) robotsTag.setAttribute('content', previous);
      else robotsTag?.parentNode?.removeChild(robotsTag);
    };
  }, []);

  const fetchInvitation = useCallback(
    () => axios.get(`${API_URL}/api/sdc-invitation/${token}`),
    [token]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchInvitation();
        if (cancelled) return;
        setInvitation(res.data?.invitation);
        setPlans(res.data?.plans || []);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 404
            ? 'This payment link is invalid or has expired.'
            : err?.response?.data?.detail || 'Could not load your payment page.'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchInvitation]);

  const handleProceed = async () => {
    if (!selectedPlan) {
      toast.error('Please choose a payment plan');
      return;
    }
    if (!acceptedTerms || !acceptedProgram) {
      toast.error('Please accept both agreements to continue');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/sdc-invitation/${token}/accept`,
        {
          plan_id: selectedPlan,
          terms_accepted: true,
        }
      );
      const url = res?.data?.checkout_url;
      if (!url) throw new Error('No checkout URL returned');
      toast.success('Redirecting to secure checkout…');
      // Tiny delay so the toast is seen
      setTimeout(() => {
        window.location.href = url;
      }, 700);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not record acceptance. Please try again.');
      setSubmitting(false);
    }
  };

  const isExpired = invitation?.status === 'expired';
  const isRevoked = invitation?.status === 'revoked';
  const alreadyAccepted = invitation?.status === 'accepted';

  return (
    <div
      className="min-h-screen"
      style={{
        background:
          'radial-gradient(circle at 20% 0%, rgba(192,108,84,0.08), transparent 40%), #F9F8F6',
        fontFamily: "'Manrope', sans-serif",
        color: '#1A3C34',
      }}
      data-testid="sdc-payment-page"
    >
      {/* Minimal branded header (no nav) */}
      <header
        className="px-6 py-5 border-b"
        style={{ borderColor: 'rgba(26,60,52,0.08)', backgroundColor: '#ffffff' }}
      >
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <p
              className="text-xs uppercase tracking-[3px]"
              style={{ color: 'rgba(26,60,52,0.5)' }}
            >
              Sister Diamonds
            </p>
            <p
              className="text-base font-medium"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#1A3C34' }}
            >
              The Collective — Private Payment Page
            </p>
          </div>
          <Lock className="h-5 w-5" style={{ color: 'rgba(26,60,52,0.5)' }} />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#C06C54' }} />
          </div>
        )}

        {!loading && error && (
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-8 pb-8 text-center">
              <p className="text-lg" style={{ color: '#C06C54' }}>{error}</p>
              <p className="text-sm mt-3" style={{ color: 'rgba(26,60,52,0.6)' }}>
                If you believe this is a mistake, please reply to your invitation email or
                contact <a href="mailto:support@sisterdiamonds.com" className="underline" style={{ color: '#C06C54' }}>support@sisterdiamonds.com</a>.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && invitation && (isExpired || isRevoked) && (
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-8 pb-8 text-center">
              <p className="text-lg" style={{ color: '#C06C54' }}>
                {isRevoked
                  ? 'This payment link has been revoked.'
                  : 'This payment link has expired.'}
              </p>
              <p className="text-sm mt-3" style={{ color: 'rgba(26,60,52,0.6)' }}>
                Please reach out to <a href="mailto:support@sisterdiamonds.com" className="underline" style={{ color: '#C06C54' }}>support@sisterdiamonds.com</a> to request a new one.
              </p>
            </CardContent>
          </Card>
        )}

        {!loading && invitation && !error && !isExpired && !isRevoked && (
          <>
            {/* Welcome */}
            <div className="text-center mb-10">
              <p
                className="text-xs uppercase tracking-[4px] mb-3"
                style={{ color: '#C06C54' }}
              >
                You're invited
              </p>
              <h1
                className="text-3xl sm:text-4xl mb-3"
                style={{
                  fontFamily: "'Cormorant Garamond', Georgia, serif",
                  fontWeight: 500,
                  color: '#1A3C34',
                }}
              >
                Welcome to the table, {(invitation.applicant_name || 'Sister').split(' ')[0]}.
              </h1>
              <p
                className="text-base max-w-xl mx-auto leading-relaxed"
                style={{ color: 'rgba(26,60,52,0.7)' }}
              >
                Crystal &amp; Dr. Nicole are honored to invite you into the Sister Diamonds
                Collective. Choose your payment plan below, accept the program agreements,
                and you'll be taken straight to a secure checkout to finalize your seat.
              </p>
            </div>

            {invitation.custom_message && (
              <div
                className="mb-8 p-5 rounded-xl"
                style={{
                  backgroundColor: '#FFF7F2',
                  borderLeft: '4px solid #C06C54',
                }}
              >
                <p
                  className="text-xs uppercase tracking-wide mb-2"
                  style={{ color: '#C06C54' }}
                >
                  A note from your hosts
                </p>
                <p className="text-sm whitespace-pre-line italic" style={{ color: '#1A3C34' }}>
                  &ldquo;{invitation.custom_message}&rdquo;
                </p>
              </div>
            )}

            {/* What's included reminder */}
            <Card className="border-0 shadow-sm mb-8" style={{ backgroundColor: '#1A3C34', color: '#ffffff' }}>
              <CardContent className="pt-6 pb-6">
                <p className="text-xs uppercase tracking-[3px] mb-3" style={{ color: '#E8DCCA' }}>
                  Your SDC seat includes
                </p>
                <ul className="space-y-2">
                  {[
                    '18 individual coaching sessions with a Volition coach',
                    '4 coaching sessions with the Wonder Twins',
                    'All-access to the SD Portal Vault',
                    '1-year membership in Inner Circle',
                    'Up to 2 in-person VIP Masterminds',
                    'Special pricing on retreats',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <Crown className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#C06C54' }} />
                      <span style={{ color: '#F9F8F6' }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Plan selection */}
            <h2
              className="text-2xl mb-4"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: '#1A3C34',
              }}
            >
              Choose your payment plan
            </h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-10">
              {plans.map((plan) => {
                const active = selectedPlan === plan.id;
                return (
                  <button
                    key={plan.id}
                    type="button"
                    onClick={() => setSelectedPlan(plan.id)}
                    className="text-left rounded-xl p-5 transition-all"
                    style={{
                      backgroundColor: active ? '#1A3C34' : '#ffffff',
                      color: active ? '#ffffff' : '#1A3C34',
                      border: active ? '2px solid #C06C54' : '1px solid rgba(26,60,52,0.12)',
                      boxShadow: active ? '0 8px 28px rgba(26,60,52,0.18)' : '0 2px 6px rgba(26,60,52,0.06)',
                    }}
                    data-testid={`plan-${plan.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p
                        className="text-base font-semibold"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                      >
                        {plan.label}
                      </p>
                      {plan.savings && (
                        <span
                          className="text-[10px] uppercase tracking-wide px-2 py-1 rounded-full"
                          style={{
                            backgroundColor: '#C06C54',
                            color: '#ffffff',
                          }}
                        >
                          Best value
                        </span>
                      )}
                    </div>
                    <p className="text-sm mb-2 opacity-90">{plan.summary}</p>
                    {plan.savings && (
                      <p
                        className="text-xs"
                        style={{ color: active ? '#E8DCCA' : '#C06C54' }}
                      >
                        {plan.savings}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Terms */}
            <div
              className="rounded-xl p-6 mb-8"
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid rgba(26,60,52,0.1)',
              }}
            >
              <p
                className="text-xs uppercase tracking-[3px] mb-4"
                style={{ color: 'rgba(26,60,52,0.6)' }}
              >
                Required acknowledgments
              </p>
              <label className="flex items-start gap-3 mb-4 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedProgram}
                  onChange={(e) => setAcceptedProgram(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded accent-[#C06C54]"
                  data-testid="accept-program-checkbox"
                />
                <span className="text-sm leading-relaxed">
                  I understand the Sister Diamonds Collective is a 12-month commitment, that
                  payment plans are non-cancelable, and that all coaching sessions, mastermind
                  events, and retreat-related benefits described here are part of the membership
                  experience.
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded accent-[#C06C54]"
                  data-testid="accept-terms-checkbox"
                />
                <span className="text-sm leading-relaxed">
                  I have read and agree to the Sister Diamonds and Volition Enterprises{' '}
                  <Link
                    to="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                    style={{ color: '#C06C54' }}
                    data-testid="open-terms-link"
                  >
                    Terms &amp; Conditions
                  </Link>{' '}
                  and{' '}
                  <Link
                    to="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium"
                    style={{ color: '#C06C54' }}
                  >
                    Privacy Policy
                  </Link>
                  .
                </span>
              </label>
            </div>

            {alreadyAccepted && (
              <p
                className="text-sm text-center mb-4"
                style={{ color: 'rgba(26,60,52,0.6)' }}
              >
                You've already submitted an acceptance — selecting a plan again will update
                your record.
              </p>
            )}

            {/* CTA */}
            <Button
              onClick={handleProceed}
              disabled={submitting || !selectedPlan || !acceptedTerms || !acceptedProgram}
              className="w-full py-6 text-base"
              style={{
                backgroundColor: '#C06C54',
                color: '#ffffff',
                fontFamily: "'Manrope', sans-serif",
                letterSpacing: '1px',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
              data-testid="proceed-to-checkout-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" /> Recording your acceptance…
                </>
              ) : (
                <>
                  Proceed to secure checkout <ExternalLink className="h-4 w-4 ml-2" />
                </>
              )}
            </Button>

            <div className="flex items-center justify-center gap-2 mt-5 text-xs" style={{ color: 'rgba(26,60,52,0.5)' }}>
              <ShieldCheck className="h-3.5 w-3.5" />
              Checkout is processed by Ontraport on a secured page.
            </div>
          </>
        )}
      </main>

      <footer
        className="px-6 py-6 mt-12 text-center text-xs"
        style={{ color: 'rgba(26,60,52,0.4)' }}
      >
        © {new Date().getFullYear()} Volition Enterprises, Inc. — operator of the Sister Diamonds brand.
      </footer>
    </div>
  );
};

export default SDCPaymentPage;
